import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Plus, Trash2, Edit3, Image as ImageIcon, Loader2, CheckCircle, X, AlignLeft, Beaker, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, storage } from '../firebase/firebase-config';
import './ProductManager.css';

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all'); 
  const [editingId, setEditingId] = useState(null); 
  
  // Tamamen Bigma Katalog yapısına göre güncellenmiş başlangıç state
  const initialProductState = { 
    title: '', // Ürün Adı
    categoryId: '', 
    animalGroup: '', // Hayvan Grubu
    packaging: '', // Ambalaj
    license: '', // Ruhsat/Not
    barcode: '', // Barkod
    imageUrl: '', 
    description: '', // Kısa Açıklama (Hero alanı için)
    usageGuide: '', // Kullanım Kılavuzu
    // Teknik Analiz Nesnesi (Fotoğraftaki tablo ile birebir)
    analysis: {
      hamProtein: '',
      hamYag: '',
      hamSeluloz: '',
      hamKul: '',
      nem: '',
      vitaminMineral: ''
    }
  };

  const [newProduct, setNewProduct] = useState(initialProductState);
  const [loading, setLoading] = useState({ submit: false, img: false });
  const [status, setStatus] = useState('');

  const fetchData = async () => {
    const catSnap = await getDocs(query(collection(db, "categories"), orderBy("order", "asc")));
    setCategories(catSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    const prodSnap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
    setProducts(prodSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => { fetchData(); }, []);

  const filteredProducts = selectedFilter === 'all' 
    ? products 
    : products.filter(p => p.categoryId === selectedFilter);

  const handleAnalysisChange = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      analysis: { ...prev.analysis, [field]: value }
    }));
  };

  const handleUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setLoading(prev => ({ ...prev, img: true }));
    const sRef = ref(storage, `products/${Date.now()}_${selectedFile.name}`);
    try {
      await uploadBytes(sRef, selectedFile);
      const url = await getDownloadURL(sRef);
      setNewProduct(prev => ({ ...prev, imageUrl: url }));
    } catch (err) { console.error(err); }
    setLoading(prev => ({ ...prev, img: false }));
  };

  const resetForm = () => {
    setNewProduct(initialProductState);
    setEditingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, submit: true }));
    try {
      const productData = { ...newProduct, updatedAt: new Date() };
      if (editingId) {
        await updateDoc(doc(db, "products", editingId), productData);
        setStatus('updated');
      } else {
        await addDoc(collection(db, "products"), { ...productData, createdAt: new Date() });
        setStatus('success');
      }
      resetForm();
      fetchData();
      setTimeout(() => setStatus(''), 3000);
    } catch (err) { console.error(err); }
    setLoading(prev => ({ ...prev, submit: false }));
  };

  const startEdit = (prod) => {
    setNewProduct({
      ...initialProductState,
      ...prod,
      analysis: { ...initialProductState.analysis, ...(prod.analysis || {}) }
    });
    setEditingId(prod.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bu ürünü silmek istediğine emin misin?")) {
      await deleteDoc(doc(db, "products", id));
      fetchData();
    }
  };

  return (
    <div className="panel-box">
      <AnimatePresence>
        {status && (
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 50 }} className="status-toast">
            <CheckCircle size={22} /> <span>{status === 'updated' ? 'Ürün Güncellendi!' : 'Ürün Eklendi!'}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="admin-header-flex">
        <h3>{editingId ? 'Bigma Ürün Düzenle' : 'Bigma Yeni Ürün Ekle'}</h3>
      </div>

      <form className="admin-form" onSubmit={handleSave}>
        <div className="product-upload">
          {newProduct.imageUrl ? <img src={newProduct.imageUrl} alt="Önizleme" /> : <div className="placeholder-content"><ImageIcon size={32} /><span>Katalog Görseli</span></div>}
          {loading.img && <div className="loading-overlay-abs"><Loader2 className="animate-spin" /></div>}
          <input type="file" onChange={handleUpload} id="prod-img" hidden />
          <label htmlFor="prod-img" className="full-click-label"></label>
        </div>

        <div className="form-right-content">
          <div className="admin-form-grid">
            <div className="form-group"><label>Ürün Başlığı</label><input type="text" value={newProduct.title} onChange={e => setNewProduct({...newProduct, title: e.target.value})} placeholder="Örn: NIRVANA MILK" required /></div>
            <div className="form-group">
              <label>Kategori (Sitedeki Bölüm)</label>
              <select value={newProduct.categoryId} onChange={e => setNewProduct({...newProduct, categoryId: e.target.value})} required>
                <option value="">Seçin</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Hayvan Grubu</label><input type="text" value={newProduct.animalGroup} onChange={e => setNewProduct({...newProduct, animalGroup: e.target.value})} placeholder="Örn: Süt İneği" required /></div>
            <div className="form-group"><label>Ambalaj Şekli</label><input type="text" value={newProduct.packaging} onChange={e => setNewProduct({...newProduct, packaging: e.target.value})} placeholder="Örn: 25 kg Kraft Torba" /></div>
            <div className="form-group"><label>Ruhsat / Not</label><input type="text" value={newProduct.license} onChange={e => setNewProduct({...newProduct, license: e.target.value})} placeholder="Örn: TR:5800051" /></div>
            <div className="form-group"><label>Barkod No</label><input type="text" value={newProduct.barcode} onChange={e => setNewProduct({...newProduct, barcode: e.target.value})} placeholder="8 683860..." /></div>
          </div>

          {/* TEKNİK ANALİZ TABLOSU VERİLERİ */}
          <div className="analysis-inputs-section">
            <div className="section-title-mini"><Beaker size={16}/> Teknik Analiz (Tablo Verileri)</div>
            <div className="analysis-grid">
              <div className="form-group-mini"><label>Ham Protein</label><input type="text" value={newProduct.analysis.hamProtein} onChange={e => handleAnalysisChange('hamProtein', e.target.value)} placeholder="% Değer" /></div>
              <div className="form-group-mini"><label>Ham Yağ</label><input type="text" value={newProduct.analysis.hamYag} onChange={e => handleAnalysisChange('hamYag', e.target.value)} placeholder="% Değer" /></div>
              <div className="form-group-mini"><label>Ham Selüloz</label><input type="text" value={newProduct.analysis.hamSeluloz} onChange={e => handleAnalysisChange('hamSeluloz', e.target.value)} placeholder="% Değer" /></div>
              <div className="form-group-mini"><label>Ham Kül</label><input type="text" value={newProduct.analysis.hamKul} onChange={e => handleAnalysisChange('hamKul', e.target.value)} placeholder="% Değer" /></div>
              <div className="form-group-mini"><label>Nem</label><input type="text" value={newProduct.analysis.nem} onChange={e => handleAnalysisChange('nem', e.target.value)} placeholder="Maks. %12 vb." /></div>
              <div className="form-group-mini"><label>Vitamin-Mineral</label><input type="text" value={newProduct.analysis.vitaminMineral} onChange={e => handleAnalysisChange('vitaminMineral', e.target.value)} placeholder="Destekli/Var" /></div>
            </div>
          </div>

          <div className="form-group full-width-field">
            <label><AlignLeft size={16}/> Hero Kısa Açıklama</label>
            <input type="text" value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} placeholder="Başlığın altındaki tek satırlık yazı..." />
          </div>

          <div className="form-group full-width-field">
            <label><FileText size={16}/> Kullanım Kılavuzu (Detaylı Metin)</label>
            <textarea value={newProduct.usageGuide} onChange={e => setNewProduct({...newProduct, usageGuide: e.target.value})} rows={4} placeholder="Hayvan türüne göre kullanım miktarları, saklama koşulları..." className="form-textarea" />
          </div>

          <div className="form-actions">
             {editingId && <button type="button" className="cancel-btn" onClick={resetForm}><X size={18}/> İptal</button>}
             <button type="submit" className="save-btn" disabled={loading.submit}>
               {loading.submit ? <Loader2 className="animate-spin" /> : (editingId ? <><Edit3 size={18}/> Güncelle</> : <><Plus size={18}/> Kaydet</>)}
             </button>
          </div>
        </div>
      </form>

      <hr className="panel-divider" />

      {/* Ürün Listesi... (Değişmedi ama h5 başlıkları artık title gösteriyor) */}
      <div className="admin-list-section">
        <div className="list-header-flex">
          <h4>Bigma Ürün Portföyü ({filteredProducts.length})</h4>
          <div className="filter-bar-modern">
            <button className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`} onClick={() => setSelectedFilter('all')}>Tümü</button>
            {categories.map(cat => (
              <button key={cat.id} className={`filter-btn ${selectedFilter === cat.id ? 'active' : ''}`} onClick={() => setSelectedFilter(cat.id)}>{cat.title}</button>
            ))}
          </div>
        </div>

        <div className="admin-data-list">
          {filteredProducts.map(prod => (
            <div key={prod.id} className="admin-list-item">
              <img src={prod.imageUrl} alt={prod.title} className="list-thumb" />
              <div className="list-info">
                <h5>{prod.title}</h5>
                <span>{categories.find(c => c.id === prod.categoryId)?.title} | {prod.animalGroup}</span>
              </div>
              <div className="list-actions">
                <button className="edit-mini-btn" onClick={() => startEdit(prod)}><Edit3 size={16}/></button>
                <button className="delete-mini-btn" onClick={() => handleDelete(prod.id)}><Trash2 size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductManager;